import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Copy,
  CheckCircle,
  Users,
  MapPin,
  Calendar,
  DollarSign,
  Music,
  Flame,
  Shirt,
  UserPlus,
  Hash,
  Mail,
  AlertCircle,
  LogOut,
  RotateCcw,
  Pencil,
  UserCheck,
  UserMinus,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { Button, Card, Input } from '../../components/ui';
import styles from './Profile.module.css';

interface Friend {
  id: string;
  display_name: string | null;
  email: string;
  isMutual: boolean;
}

export function Profile() {
  const navigate = useNavigate();
  const { user, profile, signOut, refreshProfile } = useAuth();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState(profile?.display_name || '');
  const [saving, setSaving] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  // Add friend state
  const [showAddFriend, setShowAddFriend] = useState(false);
  const [friendInput, setFriendInput] = useState('');
  const [friendInputType, setFriendInputType] = useState<'code' | 'email'>('code');
  const [friendStatus, setFriendStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [friendMessage, setFriendMessage] = useState('');

  useEffect(() => {
    if (!user) return;

    const fetchFriends = async () => {
      // Get friend IDs I added
      const { data: myFriendships } = await supabase
        .from('friendships')
        .select('friend_id')
        .eq('user_id', user.id);

      // Get people who added me (for mutual check)
      const { data: addedMe } = await supabase
        .from('friendships')
        .select('user_id')
        .eq('friend_id', user.id);

      const addedMeIds = new Set(addedMe?.map((f) => f.user_id) || []);
      const friendIds = myFriendships?.map((f) => f.friend_id) || [];

      if (friendIds.length === 0) {
        setFriends([]);
        return;
      }

      // Get user details for friends
      const { data: friendUsers } = await supabase
        .from('users')
        .select('id, display_name, email')
        .in('id', friendIds);

      if (friendUsers) {
        setFriends(
          friendUsers.map((friend) => ({
            id: friend.id,
            display_name: friend.display_name,
            email: friend.email,
            isMutual: addedMeIds.has(friend.id),
          }))
        );
      }
    };

    fetchFriends();
  }, [user]);

  useEffect(() => {
    if (profile?.display_name) {
      setDisplayName(profile.display_name);
    }
  }, [profile]);

  const copyAppCode = async () => {
    if (profile?.app_code) {
      await navigator.clipboard.writeText(profile.app_code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setSaving(true);

    await supabase
      .from('users')
      .update({ display_name: displayName })
      .eq('id', user.id);

    await refreshProfile();
    setSaving(false);
    setEditing(false);
  };

  const handleAddFriend = async () => {
    if (!friendInput.trim()) return;

    setFriendStatus('loading');

    let friendUser: { id: string; display_name: string | null; email: string } | null = null;

    if (friendInputType === 'code') {
      const { data, error } = await supabase
        .from('users')
        .select('id, display_name, email')
        .eq('app_code', friendInput.toUpperCase().trim())
        .single();

      if (error || !data) {
        setFriendStatus('error');
        setFriendMessage('No user found with that code');
        setTimeout(() => { setFriendStatus('idle'); setFriendMessage(''); }, 3000);
        return;
      }
      friendUser = data;
    } else {
      const email = friendInput.toLowerCase().trim();
      const { data, error } = await supabase
        .from('users')
        .select('id, display_name, email')
        .eq('email', email)
        .single();

      if (error || !data) {
        setFriendStatus('error');
        setFriendMessage('No user found with that email');
        setTimeout(() => { setFriendStatus('idle'); setFriendMessage(''); }, 3000);
        return;
      }
      friendUser = data;
    }

    if (friendUser.id === user?.id) {
      setFriendStatus('error');
      setFriendMessage("That's you!");
      setTimeout(() => { setFriendStatus('idle'); setFriendMessage(''); }, 3000);
      return;
    }

    // Check if already added
    const { data: existing } = await supabase
      .from('friendships')
      .select('id')
      .eq('user_id', user!.id)
      .eq('friend_id', friendUser.id)
      .single();

    if (existing) {
      setFriendStatus('error');
      setFriendMessage('Already added!');
      setTimeout(() => { setFriendStatus('idle'); setFriendMessage(''); }, 3000);
      return;
    }

    // Check if they added me (will become mutual)
    const { data: theyAddedMe } = await supabase
      .from('friendships')
      .select('id')
      .eq('user_id', friendUser.id)
      .eq('friend_id', user!.id)
      .single();

    // Create friendship
    const { error: friendshipError } = await supabase.from('friendships').insert({
      user_id: user!.id,
      friend_id: friendUser.id,
    });

    if (friendshipError) {
      setFriendStatus('error');
      setFriendMessage('Could not add friend');
      setTimeout(() => { setFriendStatus('idle'); setFriendMessage(''); }, 3000);
      return;
    }

    const friendName = friendUser.display_name || friendUser.email;
    setFriendStatus('success');
    setFriendMessage(theyAddedMe ? `You and ${friendName} are now mutuals!` : `Added ${friendName}!`);

    // Add to local state
    setFriends([
      ...friends,
      {
        id: friendUser.id,
        display_name: friendUser.display_name,
        email: friendUser.email,
        isMutual: !!theyAddedMe,
      },
    ]);

    setFriendInput('');
    setTimeout(() => { setFriendStatus('idle'); setFriendMessage(''); }, 3000);
  };

  const handleRemoveFriend = async (friendId: string) => {
    const { error } = await supabase
      .from('friendships')
      .delete()
      .eq('user_id', user!.id)
      .eq('friend_id', friendId);

    if (!error) {
      setFriends(friends.filter((f) => f.id !== friendId));
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const vibeLabels: Record<string, string> = {
    bacchanal: 'Bacchanal',
    pretty: 'Pretty Mas',
    music: 'Music',
    social: 'Social',
    cultural: 'Cultural',
  };

  const musicLabels: Record<string, string> = {
    soca: 'Soca',
    dancehall: 'Dancehall',
    afrobeats: 'Afrobeats',
    bouyon: 'Bouyon',
    everything: 'Everything',
  };

  const budgetLabels: Record<string, string> = {
    under_500: 'Under $500',
    '500_800': '$500 - $800',
    '800_1200': '$800 - $1200',
    '1200_plus': '$1200+',
    unlimited: 'Unlimited',
  };

  const costumeLabels: Record<string, string> = {
    frontline: 'Frontline',
    midline: 'Midline',
    backline: 'Backline',
    no_preference: 'No preference',
  };

  const ageLabels: Record<string, string> = {
    '18_24': '18-24',
    '25_34': '25-34',
    '35_44': '35-44',
    '45_plus': '45+',
  };

  const mutualCount = friends.filter((f) => f.isMutual).length;

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate('/discover')}>
          <ArrowLeft size={20} />
        </button>
        <h1>Profile</h1>
        <div style={{ width: 40 }} />
      </header>

      {/* Profile info */}
      <section className={styles.section}>
        <Card className={styles.profileCard}>
          <div className={styles.avatar}>
            {profile?.display_name?.[0] || user?.email?.[0] || '?'}
          </div>
          {editing ? (
            <div className={styles.editForm}>
              <Input
                label="Display Name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your name"
              />
              <div className={styles.editActions}>
                <Button variant="ghost" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveProfile} loading={saving}>
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <>
              <h2 className={styles.name}>{profile?.display_name || 'Masquerader'}</h2>
              <p className={styles.email}>{user?.email}</p>
              <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                <Pencil size={14} />
                Edit Profile
              </Button>
            </>
          )}
        </Card>
      </section>

      {/* Your App Code */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <Hash size={16} />
          Your Friend Code
        </h3>
        <Card>
          <p className={styles.inviteText}>
            Share your code so friends can add you on Mas Match
          </p>
          <div className={styles.codeDisplay}>
            <span className={styles.appCode}>{profile?.app_code || '------'}</span>
            <Button size="sm" onClick={copyAppCode}>
              {codeCopied ? <CheckCircle size={16} /> : <Copy size={16} />}
              {codeCopied ? 'Copied!' : 'Copy'}
            </Button>
          </div>
        </Card>
      </section>

      {/* Friends */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>
            <Users size={16} />
            Friends ({friends.length})
            {mutualCount > 0 && <span className={styles.mutualBadge}>{mutualCount} mutual</span>}
          </h3>
          <Button variant="ghost" size="sm" onClick={() => setShowAddFriend(!showAddFriend)}>
            <UserPlus size={14} />
            Add
          </Button>
        </div>

        {showAddFriend && (
          <Card className={styles.addFriendCard}>
            <div className={styles.inputTypeToggle}>
              <button
                className={`${styles.toggleBtn} ${friendInputType === 'code' ? styles.active : ''}`}
                onClick={() => setFriendInputType('code')}
              >
                <Hash size={14} />
                Code
              </button>
              <button
                className={`${styles.toggleBtn} ${friendInputType === 'email' ? styles.active : ''}`}
                onClick={() => setFriendInputType('email')}
              >
                <Mail size={14} />
                Email
              </button>
            </div>
            <div className={styles.addFriendInput}>
              <input
                type={friendInputType === 'email' ? 'email' : 'text'}
                className={styles.input}
                placeholder={friendInputType === 'code' ? "Enter friend's code" : "Enter friend's email"}
                value={friendInput}
                onChange={(e) => setFriendInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddFriend()}
              />
              <button
                className={styles.addBtn}
                onClick={handleAddFriend}
                disabled={!friendInput.trim() || friendStatus === 'loading'}
              >
                <UserPlus size={16} />
              </button>
            </div>
            {friendMessage && (
              <div className={`${styles.friendMessage} ${styles[friendStatus]}`}>
                {friendStatus === 'success' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                {friendMessage}
              </div>
            )}
            <p className={styles.friendNote}>Friends must already have a Mas Match account</p>
          </Card>
        )}

        {friends.length > 0 && (
          <Card padding="sm">
            {friends.map((friend) => (
              <div key={friend.id} className={styles.friendItem}>
                <div className={styles.friendAvatar}>
                  {friend.display_name?.[0] || friend.email?.[0] || '?'}
                </div>
                <div className={styles.friendInfo}>
                  <span className={styles.friendName}>{friend.display_name || friend.email}</span>
                  {friend.isMutual && (
                    <span className={styles.mutualTag}>
                      <UserCheck size={10} />
                      Mutual
                    </span>
                  )}
                </div>
                <button
                  className={styles.removeFriendBtn}
                  onClick={() => handleRemoveFriend(friend.id)}
                  title="Remove friend"
                >
                  <UserMinus size={14} />
                </button>
              </div>
            ))}
          </Card>
        )}

        {friends.length === 0 && !showAddFriend && (
          <Card>
            <p className={styles.emptyText}>No friends yet. Add friends to see which bands they matched with!</p>
          </Card>
        )}
      </section>

      {/* Preferences summary */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <Flame size={16} />
          Your Preferences
        </h3>
        <Card>
          <div className={styles.prefItem}>
            <span className={styles.prefLabel}>
              <MapPin size={14} />
              Home
            </span>
            <span>{profile?.location || '-'}</span>
          </div>
          <div className={styles.prefItem}>
            <span className={styles.prefLabel}>
              <MapPin size={14} />
              Carnival Pickup
            </span>
            <span>{profile?.trinidad_area ? profile.trinidad_area.split(' (')[0] : '-'}</span>
          </div>
          <div className={styles.prefItem}>
            <span className={styles.prefLabel}>
              <Calendar size={14} />
              Age Range
            </span>
            <span>{profile?.age_range ? ageLabels[profile.age_range] : '-'}</span>
          </div>
          <div className={styles.prefItem}>
            <span className={styles.prefLabel}>
              <DollarSign size={14} />
              Budget
            </span>
            <span>{profile?.budget_range ? budgetLabels[profile.budget_range] : '-'}</span>
          </div>
          <div className={styles.prefItem}>
            <span className={styles.prefLabel}>
              <Shirt size={14} />
              Costume
            </span>
            <span>{profile?.costume_preference ? costumeLabels[profile.costume_preference] : '-'}</span>
          </div>
          <div className={styles.prefItem}>
            <span className={styles.prefLabel}>
              <Flame size={14} />
              Vibes
            </span>
            <span>
              {profile?.vibe_preferences?.map((v) => vibeLabels[v]).join(', ') || '-'}
            </span>
          </div>
          <div className={styles.prefItem}>
            <span className={styles.prefLabel}>
              <Music size={14} />
              Music
            </span>
            <span>
              {profile?.music_preferences?.map((m) => musicLabels[m]).join(', ') || '-'}
            </span>
          </div>
          {profile?.past_bands && profile.past_bands.length > 0 && (
            <div className={styles.prefItem}>
              <span className={styles.prefLabel}>
                <Users size={14} />
                Past Bands
              </span>
              <span>{profile.past_bands.join(', ')}</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/onboarding')}
            style={{ marginTop: 'var(--space-sm)' }}
          >
            <RotateCcw size={14} />
            Retake Quiz
          </Button>
        </Card>
      </section>

      {/* Logout */}
      <section className={styles.section}>
        <Button variant="secondary" fullWidth onClick={handleLogout}>
          <LogOut size={16} />
          Sign Out
        </Button>
      </section>
    </div>
  );
}
